<script lang="ts">
    import {Flex, KpLoadableContainer, Spacer} from 'shared/layouts';
    import type {SimpleFond} from 'src/typings';
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {DayOptimisationOverviewResponse, GroupedProjectItemsResponse} from '../../types';
    import {getProjectReportOptimisationContext} from '../../project-report-optimisation-context';
    import {get} from 'svelte/store';
    import {onMount} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import OptimisationOverviewContainer from '../components/OptimisationOverviewContainer.svelte';
    import OptimisationOverviewHeading from '../components/OptimisationOverviewHeading.svelte';
    import ItemOverviewJobTypeCard from '../components/ItemOverviewJobTypeCard.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpStyledSelect from 'shared/ui-widgets/select/KpStyledSelect.svelte';
    import ProjectItemSelector from '../components/ProjectItemSelector.svelte';

    const context = getProjectReportOptimisationContext();
    const openOverviewProjectItem = get(context.openOverviewSignal$);

    let loadingProjectItems = true;
    let groupedProjectItems: GroupedProjectItemsResponse | null = null;

    let selectedFond: SimpleFond | null = null;
    let selectedProjectItem = openOverviewProjectItem;

    let loadingOptimisationOverview = true;
    let dayOptimisationOverviewResponse: DayOptimisationOverviewResponse | null;

    onMount(async () => {
        if (!exists(openOverviewProjectItem)) {
            const loadedGroupedProjectItems = await context.service.getGroupedProjectItems(context.projectRecord);

            if (exists(loadedGroupedProjectItems)) {
                groupedProjectItems = loadedGroupedProjectItems;
                selectedFond = groupedProjectItems.uniqueItemFonds[0];
            }
        }

        if (exists(openOverviewProjectItem)) {
            selectedFond = openOverviewProjectItem.fond;
        }

        loadingProjectItems = false;

        if (!exists(selectedProjectItem)) {
            loadingOptimisationOverview = false;
            return;
        }

        await loadItemOptimisationOverview(selectedProjectItem);
    });

    async function loadItemOptimisationOverview(projectItem: RecordRow) {
        if (!exists(projectItem)) {
            return;
        }

        loadingOptimisationOverview = true;
        const projectRecord = context.projectRecord;
        dayOptimisationOverviewResponse = await context.service.getItemOverview(projectRecord, projectItem);
        loadingOptimisationOverview = false;
    }

    function getHeadingText(businessItemRecord: RecordRow): string {
        if (!exists(businessItemRecord)) {
            return 'Přehled';
        }

        return `Přehled pro ${businessItemRecord.name}`;
    }

    const handleProjectItemSelected = async (event: CustomEvent<RecordRow | null>) => {
        selectedProjectItem = event.detail;
        dayOptimisationOverviewResponse = null;
        await loadItemOptimisationOverview(selectedProjectItem);
    };

    const handleFondChange = () => {
        selectedProjectItem = null;
        dayOptimisationOverviewResponse = null;
    };

    function filterItemsByFond(items: RecordRow[], fond: SimpleFond): RecordRow[] {
        return items.filter((item) => item.fond.id === fond.id);
    }

    function getPrintButtonSettings(item: RecordRow | null) {
        if (!exists(item)) {
            return {
                label: 'Export',
                templateName: '',
                recordId: undefined,
                disabled: true
            };
        }

        return {
            label: 'Export položky',
            templateName: '',
            recordId: item.id,
            disabled: true
        };
    }
</script>

<OptimisationOverviewContainer>
    {#if !exists(openOverviewProjectItem)}
        <KpLoadableContainer fillAvailableSpace={loadingProjectItems || !exists(selectedFond)}
                             loading={loadingProjectItems}
                             loadError={!loadingProjectItems && !exists(groupedProjectItems)}>

            <Flex direction="column" gap="sm" alignItems="flex-start">
                <label class="text-muted" for="project-item-selector">
                    <small>Fond položek</small>
                </label>

                <KpStyledSelect id="project-item-selector" bind:value={selectedFond} on:change={handleFondChange}>
                    {#each groupedProjectItems.uniqueItemFonds as fond(fond.id)}
                        <option value={fond}>
                            {pipe(fond, loc())}
                        </option>
                    {/each}

                    {#if groupedProjectItems.uniqueItemFonds.length === 0}
                        <option value={null} selected>
                            Neexistují žádné položky
                        </option>
                    {/if}
                </KpStyledSelect>
            </Flex>

            <Spacer direction="vertical" size="xl"/>

            {#if exists(selectedFond)}
                <Flex direction="column" gap="xl">
                    <ProjectItemSelector
                            projectItems={filterItemsByFond(groupedProjectItems.businessItems, selectedFond)}
                            selectedProjectItem={selectedProjectItem}
                            fond={selectedFond}
                            on:project-item-selected={handleProjectItemSelected}/>
                </Flex>
            {:else}
                <IconedContent icon="info" orientation="vertical" fillAvailableSpace>
                    <span class="load-error-label">Nejdříve vyberte fond položek</span>
                </IconedContent>
            {/if}
        </KpLoadableContainer>
    {/if}

    {#if !exists(selectedProjectItem)}
        {#if !loadingProjectItems && exists(selectedFond)}
            <IconedContent icon="info" orientation="vertical" fillAvailableSpace>
                <span class="load-error-label">Nejdříve vyberte položku</span>
            </IconedContent>
        {/if}
    {:else}
        <OptimisationOverviewHeading heading={getHeadingText(selectedProjectItem)}
                                     printButtonSettings={getPrintButtonSettings(selectedProjectItem)}/>

        <KpLoadableContainer fillAvailableSpace loading={loadingOptimisationOverview}
                             loadError={!loadingOptimisationOverview && !exists(dayOptimisationOverviewResponse)}>

            <Flex direction="column" gap="xxl" fillAvailableSpace>
                {#if dayOptimisationOverviewResponse.jobTypeSummaries.length === 0}
                    <IconedContent icon="info" orientation="vertical" fillAvailableSpace>
                        <span class="load-error-label">Nejsou k dispozici žádná data</span>
                    </IconedContent>
                {:else}
                    <Flex class="job-type-summaries-container" direction="column" gap="xl">
                        {#each dayOptimisationOverviewResponse.jobTypeSummaries as jobTypeSummary}
                            <ItemOverviewJobTypeCard {jobTypeSummary}/>
                        {/each}
                    </Flex>
                {/if}
            </Flex>
        </KpLoadableContainer>
    {/if}
</OptimisationOverviewContainer>